// src/services/bakingCcpService.js

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

// --------------------
// CONFIG (admin)
// --------------------
const getConfig = async () => {
  const res = await axios.get(`${API_URL}/api/analytics/ccp/baking/config`, {
    headers: authHeaders(),
  });
  return res.data;
};

const saveConfig = async (config) => {
  const res = await axios.put(`${API_URL}/api/analytics/ccp/baking/config`, config, {
    headers: authHeaders(),
  });
  return res.data;
};

// --------------------
// REFERENCE DATA
// --------------------
const getBatches = async () => {
  const res = await axios.get(`${API_URL}/api/analytics/bakingccp/batches`, {
    headers: authHeaders(),
  });
  return res.data;
};

// --------------------
// RUNS
// --------------------
const startRun = async (payload) => {
  const res = await axios.post(`${API_URL}/api/analytics/bakingccp/runs/start`, payload, {
    headers: authHeaders(),
  });
  return res.data;
};

const getActiveRun = async () => {
  const res = await axios.get(`${API_URL}/api/analytics/bakingccp/runs/active`, {
    headers: authHeaders(),
  });
  return res.data;
};

// ✅ QA review queue — fetch runs by status (default: COMPLETED)
const getRuns = async (status = 'COMPLETED') => {
  const res = await axios.get(`${API_URL}/api/analytics/bakingccp/runs`, {
    headers: authHeaders(),
    params: { status },
  });
  return res.data;
};

// Covers every status across both flow types (baking and pasteurization) — see FLOW_STAGES
// in backend/routes/bakingCcpRoutes.js.
const ALL_RUN_STATUSES = 'BAKING,BAKING_PAUSED,BAKING_STOPPED,PASTEURIZING,PASTEURIZING_PAUSED,AGING,READY_TO_CHURN,COMPLETED,VERIFIED';

const getRunForBatch = async (batchId) => {
  const res = await axios.get(`${API_URL}/api/analytics/bakingccp/runs`, {
    headers: authHeaders(),
    params: { batchId, status: ALL_RUN_STATUSES },
  });
  return res.data?.runs?.[0] || null;
};

const getRun = async (runId) => {
  const res = await axios.get(`${API_URL}/api/analytics/bakingccp/runs/${runId}`, {
    headers: authHeaders(),
  });
  return res.data;
};

const getRunLive = async (runId) => {
  const res = await axios.get(`${API_URL}/api/analytics/bakingccp/runs/${runId}/live`, {
    headers: authHeaders(),
  });
  return res.data;
};

// Baking controls
const pauseRun = async (runId) => {
  const res = await axios.post(`${API_URL}/api/analytics/bakingccp/runs/${runId}/pause`, {}, {
    headers: authHeaders(),
  });
  return res.data;
};

const resumeRun = async (runId) => {
  const res = await axios.post(`${API_URL}/api/analytics/bakingccp/runs/${runId}/resume`, {}, {
    headers: authHeaders(),
  });
  return res.data;
};

const stopBaking = async (runId, payload = {}) => {
  const res = await axios.post(`${API_URL}/api/analytics/bakingccp/runs/${runId}/stop-baking`, payload, {
    headers: authHeaders(),
  });
  return res.data;
};

// Pasteurization flow only — ends the aging hold, unlocks churn-batch (cart) creation.
const endAging = async (runId) => {
  const res = await axios.post(`${API_URL}/api/analytics/bakingccp/runs/${runId}/end-aging`, {}, {
    headers: authHeaders(),
  });
  return res.data;
};

const completeRun = async (runId, payload) => {
  const res = await axios.post(`${API_URL}/api/analytics/bakingccp/runs/${runId}/complete`, payload, {
    headers: authHeaders(),
  });
  return res.data;
};

// Legacy verify (kept for backward compatibility)
const verifyRun = async (runId) => {
  const res = await axios.post(`${API_URL}/api/analytics/bakingccp/runs/${runId}/verify`, {}, {
    headers: authHeaders(),
  });
  return res.data;
};

// ✅ QA: fetch ingredient lot codes for a batch
const getBatchIngredients = async (batchId) => {
  const res = await axios.get(`${API_URL}/api/logs/${batchId}/ingredients`, {
    headers: authHeaders(),
  });
  return res.data;
};

// ✅ QA: add a single new ingredient row to a batch
const addBatchIngredient = async (batchId, ingredient) => {
  const res = await axios.post(`${API_URL}/api/logs/${batchId}/ingredients`, { ingredient }, {
    headers: authHeaders(),
  });
  return res.data;
};

// ✅ QA: delete a single ingredient row from a batch
const deleteBatchIngredient = async (batchId, ingredientLotCodeId) => {
  const res = await axios.delete(`${API_URL}/api/logs/${batchId}/ingredients/${ingredientLotCodeId}`, {
    headers: authHeaders(),
  });
  return res.data;
};

// ✅ QA: bulk-update ingredient lot codes before verifying
const updateBatchIngredients = async (batchId, ingredients) => {
  const res = await axios.put(`${API_URL}/api/logs/${batchId}/ingredients`, { ingredients }, {
    headers: authHeaders(),
  });
  return res.data;
};

// QA: correct lot code and/or FG lot code before verification
const updateLotCodes = async (runId, payload) => {
  const res = await axios.patch(`${API_URL}/api/analytics/bakingccp/runs/${runId}/lot-codes`, payload, {
    headers: authHeaders(),
  });
  return res.data;
};

// ✅ QA: verify + create ProductionLog in one transaction
const verifyAndLog = async (runId, payload) => {
  const res = await axios.post(`${API_URL}/api/analytics/bakingccp/runs/${runId}/verify-and-log`, payload, {
    headers: authHeaders(),
  });
  return res.data;
};

// --------------------
// TEMPERATURE READINGS
// --------------------
const addTempReading = async (runId, payload) => {
  const res = await axios.post(
    `${API_URL}/api/analytics/bakingccp/runs/${runId}/temps`,
    payload,
    { headers: authHeaders() }
  );
  return res.data;
};

// --------------------
// CARTS
// --------------------
const createCart = async (runId, payload) => {
  const res = await axios.post(`${API_URL}/api/analytics/bakingccp/runs/${runId}/carts`, payload, {
    headers: authHeaders(),
  });
  return res.data;
};

const updateCart = async (cartId, payload) => {
  const res = await axios.put(`${API_URL}/api/analytics/bakingccp/carts/${cartId}`, payload, {
    headers: authHeaders(),
  });
  return res.data;
};

// Correct ovenOutAt/blastInAt/blastOutAt after the fact. adjustNote is required (audit trail).
const updateCartTimes = async (cartId, payload) => {
  const res = await axios.put(`${API_URL}/api/analytics/bakingccp/carts/${cartId}/times`, payload, {
    headers: authHeaders(),
  });
  return res.data;
};

const markCartBlastIn = async (cartId) => {
  const res = await axios.post(`${API_URL}/api/analytics/bakingccp/carts/${cartId}/blast-in`, {}, {
    headers: authHeaders(),
  });
  return res.data;
};

const markCartBlastOut = async (cartId) => {
  const res = await axios.post(`${API_URL}/api/analytics/bakingccp/carts/${cartId}/blast-out`, {}, {
    headers: authHeaders(),
  });
  return res.data;
};

const bakingCcpService = {
  getConfig,
  saveConfig,
  getBatches,
  startRun,
  getActiveRun,
  getRuns,
  getRunForBatch,
  getRun,
  getRunLive,
  pauseRun,
  resumeRun,
  stopBaking,
  endAging,
  completeRun,
  verifyRun,
  verifyAndLog,
  updateLotCodes,
  getBatchIngredients,
  updateBatchIngredients,
  addBatchIngredient,
  deleteBatchIngredient,
  addTempReading,
  createCart,
  updateCart,
  updateCartTimes,
  markCartBlastIn,
  markCartBlastOut,
};

export default bakingCcpService;